import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang, lp, stripLang } from "@/lib/i18n";

// Pop-up « la maison recherche des revendeurs en France ».
// Affichée une fois par session de navigation (onglet), jamais sur Revendeurs ni Panier.
const KEY = "partita-popup-revendeurs-session";

const T = {
  fr: {
    kicker: "Appel à candidatures",
    title: "Maison Partita recherche ses revendeurs en France",
    body: "Parfumeries indépendantes, concept-stores, hôtels et maisons d'hôtes de caractère : nous ouvrons notre distribution à un petit nombre de points de vente, en exclusivité territoriale.",
    cta: "Devenir revendeur",
    close: "Continuer la visite",
    aria: "Fermer",
  },
  en: {
    kicker: "Call for applications",
    title: "Maison Partita is looking for stockists in France",
    body: "Independent perfumeries, concept stores, hotels and guest houses of character: we are opening our distribution to a small number of points of sale, with territorial exclusivity.",
    cta: "Become a stockist",
    close: "Continue browsing",
    aria: "Close",
  },
};

export const RevendeursPopup = () => {
  const lang = useLang();
  const t = T[lang];
  const { pathname } = useLocation();
  const bare = stripLang(pathname);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (bare.startsWith("/revendeurs") || bare.startsWith("/panier")) return () => {};
    let seen = false;
    try { seen = sessionStorage.getItem(KEY) === "1"; } catch { /* ignore */ }
    if (seen) return () => {};
    const id = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(id);
    // Une seule évaluation par chargement de page : on ne réarme pas à chaque navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    setOpen(false);
    try { sessionStorage.setItem(KEY, "1"); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return () => {};
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-5" role="dialog" aria-modal="true" aria-labelledby="popup-revendeurs-title" data-testid="popup-revendeurs">
      <button type="button" onClick={close} aria-label={t.aria} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg border px-8 py-10 sm:px-12 sm:py-14 text-center"
        style={{ background: "#0c0c0d", borderColor: "rgba(201,168,106,0.35)", color: "#f4f4f5", fontFamily: "'Cormorant Garamond', 'Cormorant', serif" }}>
        <button type="button" onClick={close} aria-label={t.aria} data-testid="popup-close"
          className="absolute top-3 right-4 text-2xl leading-none text-zinc-500 hover:text-zinc-100">×</button>
        <p className="text-[12px] tracking-[0.34em] uppercase" style={{ color: "#C9A86A", fontFamily: "Jost, sans-serif" }}>{t.kicker}</p>
        <h2 id="popup-revendeurs-title" className="mt-5 text-2xl sm:text-3xl leading-tight tracking-[0.04em]">{t.title}</h2>
        <p className="mt-6 text-[16px] leading-[1.8] text-zinc-400" style={{ fontFamily: "Jost, sans-serif" }}>{t.body}</p>
        <div className="mt-9 flex flex-col items-center gap-4" style={{ fontFamily: "Jost, sans-serif" }}>
          <Link to={lp(lang, "/revendeurs")} onClick={close} className="val-btn" data-testid="popup-cta">{t.cta}</Link>
          <button type="button" onClick={close} className="val-link text-[12px] tracking-[0.24em] uppercase" data-testid="popup-dismiss">{t.close}</button>
        </div>
      </div>
    </div>
  );
};
